"use client";

import { ResponsiveDialog } from "@repo/ui/components/responsive-dialog";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import { useCaloriesCalculator } from "../hooks/use-calories-calculator";
import type { CaloriesRange } from "../types/calories-calculator.type";
import { findCaloriesRangeLabel } from "../utils/find-calories-range-label";
import { CaloriesCalculatorFormStep } from "./calories-calculator-form-step";
import { CaloriesCalculatorResultStep } from "./calories-calculator-result-step";

export type CaloriesCalculatorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caloriesRanges?: CaloriesRange[];
  onApplyCalories?: (label: string) => void;
};

export const CaloriesCalculatorDialog = ({
  caloriesRanges,
  onApplyCalories,
  onOpenChange,
  open,
}: CaloriesCalculatorDialogProps) => {
  const t = useTranslations("calories-calculator");
  const { form, onRecalculate, onSubmit, result } = useCaloriesCalculator(open);

  const matchedLabel =
    result !== null && onApplyCalories && caloriesRanges?.length
      ? findCaloriesRangeLabel(result, caloriesRanges)
      : null;

  const handleApplyCalories = () => {
    if (!(onApplyCalories && matchedLabel)) {
      return;
    }

    onApplyCalories(matchedLabel);
    onOpenChange(false);
  };

  const footer =
    result === null ? (
      <>
        <Button
          className="hidden px-8 py-4 md:inline-flex"
          onClick={() => onOpenChange(false)}
          type="button"
          variant="outline"
        >
          {t("actions.cancel")}
        </Button>
        <Button
          className="w-full px-8 py-4 md:w-auto"
          onClick={onSubmit}
          type="button"
          variant="secondary"
        >
          {t("actions.calculate")}
        </Button>
      </>
    ) : (
      <>
        <Button
          className={cn(
            "px-4 py-4 md:px-8",
            !matchedLabel && "hidden md:inline-flex",
          )}
          onClick={matchedLabel ? onRecalculate : () => onOpenChange(false)}
          type="button"
          variant="outline"
        >
          {matchedLabel ? t("actions.recalculate") : t("actions.cancel")}
        </Button>
        {matchedLabel ? (
          <Button
            className="px-4 py-4 md:px-8"
            onClick={handleApplyCalories}
            type="button"
            variant="secondary"
          >
            {t("actions.search", { value: matchedLabel })}
          </Button>
        ) : (
          <Button
            className="w-full px-8 py-4 md:w-auto"
            onClick={onRecalculate}
            type="button"
            variant="secondary"
          >
            {t("actions.recalculate")}
          </Button>
        )}
      </>
    );

  return (
    <ResponsiveDialog
      contentClassName={cn(
        "p-4 md:min-h-[403px] md:overflow-y-auto md:p-6",
        result !== null && "md:min-h-[380px]",
      )}
      dialogClassName="gap-0 p-0 sm:max-w-[864px]"
      drawerClassName="top-0 z-100000000 max-h-none rounded-none"
      footer={footer}
      footerClassName="flex flex-row items-center justify-between gap-4 space-x-0 border-t py-3 px-4 sm:justify-between md:px-6 md:py-4"
      headerClassName="md:border-b md:px-6 md:py-5"
      onOpenChange={onOpenChange}
      open={open}
      title={t("title")}
      titleClassName="font-medium text-xl leading-7 md:text-2xl md:leading-8"
    >
      <Form {...form}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          {result === null ? (
            <CaloriesCalculatorFormStep form={form} />
          ) : (
            <CaloriesCalculatorResultStep result={result} />
          )}
        </form>
      </Form>
    </ResponsiveDialog>
  );
};
